const ENV = import.meta.env;

export { ENV };

export * from './constants/auth-resources.constants';
export * from './constants/table-column-widths.constants';

export const DEFAULT_TOAST_MESSAGE_LIFE = 5000;

export const PAGINATOR_THRESHOLD = 20;

export const MIN_SEARCH_LENGTH = 3;

// Mirrors the backend's MAX_PROFILE_PICTURE_B64 (2 MB decoded).
export const MAX_AVATAR_SIZE_BYTES = 2_000_000;

// Mirrors the backend's MeUpdate/UserUpdate Field(max_length=...) per field - shared by
// ProfileDetailsForm.vue (both self-service and admin profile editing) and the MSW /me
// handler's 422 validation, so the two can't drift apart.
export const PROFILE_DETAILS_MAX_LENGTHS = {
  firstName: 255,
  lastName: 255,
  phoneNumber: 64,
  jobTitle: 255,
  department: 255,
} as const;

// Backend UserCreate/UserUpdate.name: the users API also sends "first last" as one field.
export const USER_FULL_NAME_MAX_LENGTH = 255;

export const DEFAULT_FIELD_SIZE = 'large';

// The client has no default timeout and both logout paths wait for this call. Kept short because
// revoking is best-effort: the IdP sign-out runs either way.
export const LOGOUT_SESSION_REVOCATION_TIMEOUT_MS = 5000;
// The client has no default timeout and the invitation page is spinner-only until this settles.
export const ACCEPT_INVITATION_TIMEOUT_MS = 10_000;
// The one /logout status that is not a failure: no session id in the token, so there is nothing
// to revoke and nothing to warn about. Full contract in docs/architecture.md.
export const LOGOUT_NOTHING_TO_REVOKE_STATUS = 400;

// The two "no platform access" answers GET /me gives: 403 when the token maps to no active
// fabric_user row, 404 when that row has no profile. Never a policy denial - /me needs only auth.
export const IDENTITY_NO_ACCOUNT_STATUSES: readonly number[] = [403, 404];

// The SDK's own contract for GET /permissions/me (classifyHttpFailure in @link11/auth-sdk): only
// 403 is "refused" (no permissions granted). Every other status, 404 included, is a rejection or
// outage, not an access decision - unlike GET /me, this endpoint has no "no profile row" 404.
export const PERMISSIONS_NO_ACCESS_STATUSES: readonly number[] = [403];

// identityGuard blocks the first render on GET /me, so bound the wait: the client has no default
// timeout, and a hang would leave a blank page forever.
export const IDENTITY_REQUEST_TIMEOUT_MS = 15000;

// The identity call. src/config/http.ts skips the generic toast for this one request, because
// /logout already explains the failure. Only the GET - PATCH/DELETE /me are user actions.
export const IDENTITY_REQUEST_PATH = '/me';

// Where the IdP returns after sign-out (postLogoutRedirectUri in src/plugins/auth.ts), and where
// identityGuard sends a repeat identity failure.
export const LOGOUT_ROUTE = '/logout';

// Where the SDK's router guard sends every auth or permissions failure (loginRoute in
// src/plugins/auth.ts), and the only page identityGuard can see a refused permissions load on.
export const LOGIN_ROUTE = '/login';

export const DASHBOARD_ROUTE = '/dashboard';

// Routes that must render without an identity, so a failure can never blank the recovery pages.
// Lives here rather than src/router/index.ts, which re-exports identityGuard: that would cycle.
export const PUBLIC_ROUTES = [LOGIN_ROUTE, LOGOUT_ROUTE, '/callback', '/silent-renew'];

// index.html repeats these and PUBLIC_ROUTES; it can't import them.
export const BOOT_LOADER_ID = 'boot-loader';
export const BOOT_ERROR_ID = 'boot-error';
export const APP_BOOTED_STORAGE_KEY = 'fabric.app-booted';

// Why the app ended the session, so /logout can explain itself. Also the latch that stops an
// identity failure from looping between the IdP and the shell (src/router/identityGuard.ts).
export const SESSION_END_REASON_STORAGE_KEY = 'fabric.session-end-reason';

// The Change Customer flyout selection. sessionStorage: survives a refresh of this tab, but not
// another tab or the next sign-in on a shared browser.
export const CUSTOMER_SCOPE_STORAGE_KEY = 'fabric.customer-scope';

// Max concurrent GET /customers/{id} (+ /children) requests fetchVisibleCustomers
// fans out per caller - see src/helpers/PermissionsUtils.ts.
export const VISIBLE_CUSTOMERS_FETCH_CONCURRENCY = 5;
